import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { telHref, formatPhoneNational, type Menu, type SiteSettings } from '@cng/shared';
import { SectionContainer } from './SectionContainer';

interface HeaderProps {
  settings: SiteSettings;
  menu: Menu | null;
}

/**
 * Reproduces the original `.cgsalon-header-*` markup so the ported stylesheet
 * applies unchanged. Navigation items come from the CMS, not from code.
 */
export function Header({ settings, menu }: HeaderProps) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navId = useId();
  const navRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // A tap on a link should leave the panel closed on the next screen.
  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    const onClick = (e: MouseEvent) => {
      if (!navRef.current?.contains(e.target as Node) && !toggleRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const items = (menu?.items ?? [])
    .filter((i) => i.enabled)
    .sort((a, b) => a.order - b.order);

  const phone = settings.primaryPhone;
  // The header button shows the national number, matching the original.
  const phoneLabel = formatPhoneNational(phone);

  return (
    // The live site places the header inside a boxed Elementor container whose
    // 10px inner padding offsets the wrapper's own negative top margin.
    <SectionContainer className="cng-header-con">
      <header className="cgsalon-header-wrapper" id="masthead">
        <div className="cgsalon-header-bar">
        <div className="cgsalon-header-row">
          <Link className="cgsalon-logo" to="/" aria-label={`${settings.businessName} — home`}>
            {settings.logo?.url ? (
              <img
                src={settings.logo.url}
                alt=""
                aria-hidden="true"
                width={settings.logo.width ?? undefined}
                height={settings.logo.height ?? undefined}
                className="cgsalon-logo-img"
              />
            ) : null}
            <span className="cgsalon-brand-name">{settings.logoText || settings.businessName}</span>
          </Link>

          <div className="cgsalon-nav-center-wrap">
            <nav
              className={`cgsalon-nav${open ? ' active' : ''}`}
              id={navId}
              ref={navRef}
              aria-label="Primary"
            >
              {/* Closing here as well as on navigation matters: tapping the link
                  for the page you are already on does not change the pathname,
                  so the panel would otherwise stay open over the content. */}
              <ul className="cgsalon-navmenu" onClick={() => setOpen(false)}>
                {items.map((item) => {
                  const isExternal = item.type === 'external' || /^https?:\/\//i.test(item.value);
                  const active = !isExternal && location.pathname === item.value;
                  return (
                    <li key={`${item.label}-${item.value}`}>
                      {isExternal ? (
                        <a
                          href={item.value}
                          target={item.newTab ? '_blank' : undefined}
                          rel={item.newTab ? 'noopener noreferrer' : undefined}
                        >
                          {item.label}
                        </a>
                      ) : (
                        <Link to={item.value} aria-current={active ? 'page' : undefined} className={active ? 'active' : undefined}>
                          {item.label}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>

            <button
              type="button"
              className="cgsalon-nav-toggle"
              ref={toggleRef}
              aria-expanded={open}
              aria-controls={navId}
              aria-label={open ? 'Close menu' : 'Open menu'}
              onClick={() => setOpen((v) => !v)}
            >
              <svg className="cgsalon-hamburger" viewBox="0 0 28 28" aria-hidden="true" focusable="false">
                {open ? (
                  <>
                    <line className="cgsalon-hamburger-bar" x1="6" y1="6" x2="22" y2="22" strokeLinecap="round" />
                    <line className="cgsalon-hamburger-bar" x1="22" y1="6" x2="6" y2="22" strokeLinecap="round" />
                  </>
                ) : (
                  <>
                    <line className="cgsalon-hamburger-bar" x1="4" y1="8" x2="24" y2="8" strokeLinecap="round" />
                    <line className="cgsalon-hamburger-bar" x1="4" y1="14" x2="24" y2="14" strokeLinecap="round" />
                    <line className="cgsalon-hamburger-bar" x1="4" y1="20" x2="24" y2="20" strokeLinecap="round" />
                  </>
                )}
              </svg>
            </button>
          </div>

          {phone ? (
            <a className="cgsalon-booknow-btn" href={telHref(phone)}>
              {settings.bookNowLabel || 'Book Now'} <span>{phoneLabel}</span>
            </a>
          ) : null}
          </div>
        </div>
      </header>
    </SectionContainer>
  );
}
